import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  UserX, Mail, Phone, Calendar, User, CheckCircle,
  Trash2, Loader2, MessageSquare, Clock, Search, X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';

const NoShowsTab = ({ trials, trialsLoading, fetchTrials }) => {
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [contactDialog, setContactDialog] = useState(null);
  const [contactNote, setContactNote] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all | not_contacted | contacted

  const noShows = trials.filter(t => t.attendance === 'no_show');

  const filtered = noShows.filter(trial => {
    const name = `${trial.student_first_name || ''} ${trial.student_last_name || ''}`.toLowerCase();
    const parent = (trial.parent_name || '').toLowerCase();
    const matchesSearch = !searchQuery || name.includes(searchQuery.toLowerCase()) || parent.includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all'
      || (filterStatus === 'contacted' && trial.contacted)
      || (filterStatus === 'not_contacted' && !trial.contacted);
    return matchesSearch && matchesStatus;
  });

  const contactedCount = noShows.filter(t => t.contacted).length;
  const notContactedCount = noShows.length - contactedCount;

  const handleMarkContacted = async () => {
    if (!contactDialog) return;
    setSavingContact(true);
    try {
      await api.patch(`/free-trials/${contactDialog.id}/mark-contacted`, {
        note: contactNote
      });
      toast.success(`Marked ${contactDialog.student_first_name} as contacted`);
      setContactDialog(null);
      setContactNote('');
      fetchTrials();
    } catch (error) {
      toast.error('Failed to mark as contacted');
    } finally {
      setSavingContact(false);
    }
  };

  const handleMarkAttended = async (trial) => {
    setUpdatingId(trial.id);
    try {
      await api.patch(`/free-trials/${trial.id}/attendance`, { attendance: 'attended' });
      toast.success(`${trial.student_first_name} corrected to Attended`);
      fetchTrials();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (trial) => {
    if (!confirm(`Delete no-show record for ${trial.student_first_name} ${trial.student_last_name}?`)) return;
    setDeletingId(trial.id);
    try {
      await api.delete(`/free-trials/${trial.id}`);
      toast.success('No-show record deleted');
      fetchTrials();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const formatTrialDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return null;
    const diff = (new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24);
    return Math.floor(diff);
  };

  const openRescheduleEmail = (trial) => {
    const trialLink = `${window.location.origin}/free-trial`;
    const subject = encodeURIComponent(`Reschedule ${trial.student_first_name}'s Free Trial - UPA`);
    const body = encodeURIComponent(
`Hi ${trial.parent_name},

We noticed ${trial.student_first_name} wasn't able to make it to their scheduled trial on ${formatTrialDate(trial.trial_date)}. We'd love to give them another chance to experience our classes!

You can easily book a new trial date here:
${trialLink}

If you have any questions, just reply to this email or give us a call.

Best regards,
The UPA Team`
    );
    window.location.href = `mailto:${trial.parent_email}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="space-y-6" data-testid="no-shows-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
            <UserX className="w-5 h-5 text-red-400" />
            Trial No-Shows
          </h2>
          <p className="text-sm text-white/50 mt-1">Students who didn't attend their scheduled trial</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchTrials}
          disabled={trialsLoading}
          className="border-[#333] text-white hover:bg-[#222]"
          data-testid="refresh-no-shows"
        >
          {trialsLoading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card
          className={`bg-[#111] border-[#333] cursor-pointer transition-all hover:border-red-500/50 ${filterStatus === 'all' ? 'ring-1 ring-red-500 border-red-500/50' : ''}`}
          onClick={() => setFilterStatus('all')}
          data-testid="filter-all-no-shows"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-white/50 uppercase tracking-wider">Total No-Shows</p>
                <p className="text-3xl font-bold text-red-400 mt-1">{noShows.length}</p>
              </div>
              <UserX className="w-10 h-10 text-red-400/20" />
            </div>
          </CardContent>
        </Card>
        <Card
          className={`bg-[#111] border-[#333] cursor-pointer transition-all hover:border-amber-500/50 ${filterStatus === 'not_contacted' ? 'ring-1 ring-amber-500 border-amber-500/50' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'not_contacted' ? 'all' : 'not_contacted')}
          data-testid="filter-not-contacted"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-white/50 uppercase tracking-wider">Not Contacted</p>
                <p className="text-3xl font-bold text-amber-400 mt-1">{notContactedCount}</p>
              </div>
              <Phone className="w-10 h-10 text-amber-400/20" />
            </div>
          </CardContent>
        </Card>
        <Card
          className={`bg-[#111] border-[#333] cursor-pointer transition-all hover:border-emerald-500/50 ${filterStatus === 'contacted' ? 'ring-1 ring-emerald-500 border-emerald-500/50' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'contacted' ? 'all' : 'contacted')}
          data-testid="filter-contacted"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-white/50 uppercase tracking-wider">Contacted</p>
                <p className="text-3xl font-bold text-emerald-400 mt-1">{contactedCount}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-400/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      {noShows.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="Search by student or parent name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#111] border border-[#333] rounded-lg pl-10 pr-10 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:border-red-500/50"
            data-testid="no-show-search"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* No Shows List */}
      {trialsLoading ? (
        <div className="flex items-center justify-center py-16 text-white/50">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading...
        </div>
      ) : noShows.length === 0 ? (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="py-16 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500/30 mx-auto mb-4" />
            <p className="text-lg text-white/70 font-medium">No trial no-shows</p>
            <p className="text-sm text-white/40 mt-1">All trial students have attended or haven't been marked yet</p>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/50">No results matching your search/filter</p>
            <Button variant="link" onClick={() => { setSearchQuery(''); setFilterStatus('all'); }} className="text-red-400 mt-2">
              Clear filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(trial => {
            const days = daysSince(trial.trial_date);
            const isRecent = days !== null && days <= 7;
            const isStale = days !== null && days > 30;

            return (
              <Card
                key={trial.id}
                className={`bg-[#111] transition-all hover:border-[#444] ${
                  trial.contacted
                    ? 'border-emerald-500/20'
                    : isRecent
                    ? 'border-red-500/40'
                    : 'border-[#333]'
                }`}
                data-testid={`no-show-card-${trial.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    {/* Student Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="font-semibold text-white text-base" data-testid={`no-show-name-${trial.id}`}>
                          {trial.student_first_name} {trial.student_last_name}
                        </h3>
                        {trial.contacted ? (
                          <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-xs">
                            Contacted
                          </Badge>
                        ) : isRecent ? (
                          <Badge className="bg-red-500/15 text-red-400 border-red-500/30 text-xs animate-pulse">
                            Action Needed
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-xs">
                            Not Contacted
                          </Badge>
                        )}
                        {isStale && !trial.contacted && (
                          <Badge className="bg-white/5 text-white/40 border-white/10 text-xs">
                            {days}d ago
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 text-sm">
                        <div className="flex items-center gap-2 text-white/60">
                          <Calendar className="w-3.5 h-3.5 text-red-400 shrink-0" />
                          <span>Trial: {formatTrialDate(trial.trial_date)}</span>
                          {days !== null && <span className="text-white/30">({days}d ago)</span>}
                        </div>
                        <div className="flex items-center gap-2 text-white/60">
                          <User className="w-3.5 h-3.5 text-[#7000FF] shrink-0" />
                          <span className="truncate">{trial.parent_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/60">
                          <Mail className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                          <a href={`mailto:${trial.parent_email}`} className="hover:text-blue-400 truncate">
                            {trial.parent_email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-white/60">
                          <Phone className="w-3.5 h-3.5 text-green-400 shrink-0" />
                          <a href={`tel:${trial.parent_phone}`} className="hover:text-green-400">
                            {trial.parent_phone}
                          </a>
                        </div>
                      </div>

                      {trial.class_interest && (
                        <p className="text-xs text-white/40 mt-1.5">
                          Interest: <span className="text-[#FF007F]">{trial.class_interest}</span>
                        </p>
                      )}

                      {trial.contacted && trial.contact_note && (
                        <div className="mt-2 bg-emerald-500/5 border border-emerald-500/20 rounded-md px-3 py-2">
                          <p className="text-xs text-emerald-400/70 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {trial.contacted_by_name && <span>{trial.contacted_by_name} &middot; </span>}
                            {trial.contacted_at && new Date(trial.contacted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </p>
                          <p className="text-sm text-emerald-300/80 mt-0.5">{trial.contact_note}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap lg:flex-col gap-2 shrink-0">
                      {!trial.contacted && (
                        <Button
                          size="sm"
                          onClick={() => { setContactDialog(trial); setContactNote(''); }}
                          className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                          data-testid={`mark-contacted-${trial.id}`}
                        >
                          <MessageSquare className="w-3.5 h-3.5 mr-1" />
                          Contacted
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openRescheduleEmail(trial)}
                        className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs"
                        data-testid={`reschedule-${trial.id}`}
                      >
                        <Mail className="w-3.5 h-3.5 mr-1" />
                        Reschedule
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.location.href = `tel:${trial.parent_phone}`}
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10 text-xs"
                      >
                        <Phone className="w-3.5 h-3.5 mr-1" />
                        Call
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkAttended(trial)}
                        disabled={updatingId === trial.id}
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                        title="Correct to Attended"
                        data-testid={`fix-attendance-${trial.id}`}
                      >
                        {updatingId === trial.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                        Fix
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(trial)}
                        disabled={deletingId === trial.id}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                        data-testid={`delete-no-show-${trial.id}`}
                      >
                        {deletingId === trial.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Mark as Contacted Dialog */}
      <Dialog open={!!contactDialog} onOpenChange={() => setContactDialog(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-400" />
              Mark as Contacted
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-white/60">
              Recording contact with parent of <span className="text-white font-medium">{contactDialog?.student_first_name} {contactDialog?.student_last_name}</span>
            </p>
            <div>
              <label className="text-sm text-white/50 mb-1.5 block">Note (optional)</label>
              <textarea
                value={contactNote}
                onChange={(e) => setContactNote(e.target.value)}
                placeholder="e.g. Spoke with parent, rescheduling for next week..."
                className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg p-3 text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500/50 resize-none"
                rows={3}
                data-testid="contact-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setContactDialog(null)}
              className="border-[#333] text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkContacted}
              disabled={savingContact}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-contacted-btn"
            >
              {savingContact ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Mark as Contacted
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NoShowsTab;
